'use client'

import { useState, useEffect, useCallback } from 'react'
import { apiFetch } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { DateInput } from '@/components/ui/date-input'
import { formatDate } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from 'sonner'
import { Loader2, Calendar, Plus, Trash2, Plane, CheckCircle2, Hourglass, CalendarRange } from 'lucide-react'
import { workingDaysBetween, todayIST } from '@/lib/date-utils'
import {
  LEAVE_MONTHLY_LIMIT,
  LEAVE_LIMIT_WARNING,
  LEAVE_LIMIT_FORECAST,
  currentMonthKey,
  formatMonthKey,
  monthKey,
  monthlyDaysUsed,
  workingDaysInMonth,
  isOverLeaveLimit,
} from '@/lib/allowance'
import { AllowanceWarning } from '@/components/ui/allowance-warning'
import { GlassCard } from '@/components/ui/glass-card'
import { KpiTile } from '@/components/ui/kpi-tile'
import { StatusPill, statusTone } from '@/components/ui/status-pill'
import { SectionHeader } from '@/components/ui/section-header'
import { PageShell } from '@/components/ui/page-shell'
import { TabLoader, EmptyState } from '@/components/ui/loading'
import { PRIMARY_BTN } from '@/components/ui/button-styles'

type CardTone = 'default' | 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan'
const toCardTone = (t: ReturnType<typeof statusTone>): CardTone => (t === 'neutral' ? 'default' : t)

interface LeaveRequest {
  id: string
  type: string
  startDate: string
  endDate: string
  reason: string
  status: string
  rejectionReason: string | null
  createdAt: string
}

export function EmployeeLeave() {
  const [leaves, setLeaves] = useState<LeaveRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    type: 'CASUAL',
    startDate: '',
    endDate: '',
    reason: ''
  })

  const fetchData = useCallback(async () => {
    try {
      const res = await apiFetch('/api/leave')
      const data = await res.json()
      if (data.leaves) setLeaves(data.leaves)
    } catch (error) {
      console.error('Error fetching leaves:', error)
      toast.error('Failed to fetch leave requests')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // The date fields render as a popover over a hidden input, which browsers
    // will not run constraint validation against — so check them here.
    if (!formData.startDate || !formData.endDate) {
      toast.error('Please select both a start and an end date')
      return
    }
    if (formData.endDate < formData.startDate) {
      toast.error('End date cannot be before the start date')
      return
    }
    if (!formData.reason.trim()) {
      toast.error('Please enter a reason for your leave request')
      return
    }

    setSubmitting(true)

    try {
      await apiFetch('/api/leave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      })

      toast.success('Leave request submitted successfully')
      fetchData()
      setDialogOpen(false)
      setFormData({ type: 'CASUAL', startDate: '', endDate: '', reason: '' })
    } catch (e: any) {
      toast.error(e.message || 'Failed to submit leave request')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = async (leaveId: string) => {
    if (!confirm('Are you sure you want to cancel this leave request?')) return

    try {
      await apiFetch(`/api/leave/${leaveId}`, { method: 'DELETE' })
      toast.success('Leave request cancelled')
      fetchData()
    } catch (e: any) {
      toast.error(e.message || 'Failed to cancel leave request')
    }
  }

  const getTypeTone = (type: string) => {
    const map: Record<string, 'red' | 'blue' | 'green' | 'neutral' | 'violet'> = {
      SICK: 'red',
      CASUAL: 'blue',
      ANNUAL: 'green',
      UNPAID: 'neutral',
      OTHER: 'violet',
    }
    return map[type] || 'neutral'
  }

  const getDaysCount = (start: string, end: string) => workingDaysBetween(start, end)

  // Policy: up to 2 approved leaves per calendar month; beyond that, additional
  // leave may be deducted from salary.
  const thisMonth = currentMonthKey()
  const daysUsedThisMonth = monthlyDaysUsed(leaves, thisMonth)
  const overLimit = isOverLeaveLimit(daysUsedThisMonth)

  // What the request being filled in would add to the month it starts in.
  const draftMonth = monthKey(formData.startDate) || thisMonth
  const draftDays =
    formData.startDate && formData.endDate && formData.endDate >= formData.startDate
      ? workingDaysInMonth(formData.startDate, formData.endDate, draftMonth)
      : 0
  const draftWouldExceed =
    draftDays > 0 && isOverLeaveLimit(monthlyDaysUsed(leaves, draftMonth) + draftDays)

  const currentYear = new Date().getFullYear()
  const pending = leaves.filter(l => l.status === 'PENDING').length
  const approvedThisYear = leaves.filter(l => {
    if (l.status !== 'APPROVED') return false
    return new Date(l.startDate).getFullYear() === currentYear
  }).reduce((sum, l) => sum + getDaysCount(l.startDate, l.endDate), 0)
  const rejected = leaves.filter(l => l.status === 'REJECTED').length

  return (
    <PageShell>
      <SectionHeader
        icon={Plane}
        title='Leave Requests'
        description='Apply for and manage your time off'
        action={
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className={PRIMARY_BTN}>
                <Plus className='mr-2 h-4 w-4' />
                Apply for Leave
              </Button>
            </DialogTrigger>
            <DialogContent className='bg-white/95 dark:bg-white/95 dark:bg-neutral-950/95 backdrop-blur-2xl border-neutral-200 dark:border-neutral-200 dark:border-white/10'>
              <DialogHeader>
                <DialogTitle className='text-neutral-900 dark:text-white'>Apply for Leave</DialogTitle>
                <DialogDescription className='text-neutral-500 dark:text-white/50'>
                  Submit a new leave request for approval
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className='space-y-4'>
                <div className='space-y-2'>
                  <Label>Leave Type</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='SICK'>Sick Leave</SelectItem>
                      <SelectItem value='CASUAL'>Casual Leave</SelectItem>
                      <SelectItem value='ANNUAL'>Annual Leave</SelectItem>
                      <SelectItem value='UNPAID'>Unpaid Leave</SelectItem>
                      <SelectItem value='OTHER'>Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  <div className='space-y-2'>
                    <Label>Start Date</Label>
                    <DateInput
                      value={formData.startDate}
                      onChange={(value) => setFormData({ ...formData, startDate: value })}
                      placeholder='Select start date'
                      required
                      minDate={todayIST()}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label>End Date</Label>
                    <DateInput
                      value={formData.endDate}
                      onChange={(value) => setFormData({ ...formData, endDate: value })}
                      placeholder='Select end date'
                      required
                      minDate={formData.startDate || todayIST()}
                    />
                  </div>
                </div>
                <div className='space-y-2'>
                  <Label>Reason</Label>
                  <Textarea
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    required
                    placeholder='Explain the reason for your leave...'
                  />
                </div>
                {draftWouldExceed && <AllowanceWarning tone='notice' message={LEAVE_LIMIT_FORECAST} />}
                <DialogFooter>
                  <Button type='submit' disabled={submitting} className={PRIMARY_BTN}>
                    {submitting ? (
                      <>
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                        Submitting...
                      </>
                    ) : (
                      'Submit Request'
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      {loading ? (
        <TabLoader />
      ) : (
        <>
      {/* KPI Row */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4'>
        <KpiTile label='Pending' value={pending} icon={Hourglass} tone='amber' hint='Awaiting approval' />
        <KpiTile
          label='This month'
          value={`${daysUsedThisMonth}/${LEAVE_MONTHLY_LIMIT}`}
          icon={CalendarRange}
          tone={overLimit ? 'red' : 'blue'}
          hint={`${formatMonthKey(thisMonth)} · approved leaves`}
        />
        <KpiTile label={`Approved ${currentYear}`} value={approvedThisYear} icon={CheckCircle2} tone='green' hint='Days taken' />
        <KpiTile label='Rejected' value={rejected} icon={Plane} tone='red' hint='All time' />
      </div>

      {overLimit && <AllowanceWarning message={LEAVE_LIMIT_WARNING} />}

      {/* List */}
      {leaves.length === 0 ? (
        <GlassCard>
          <EmptyState
            icon={Plane}
            title='No leave requests yet'
            description='Apply for leave and your requests will be listed here.'
          />
        </GlassCard>
      ) : (
        <div className='space-y-3'>
          {leaves.map((leave) => (
            <GlassCard key={leave.id} tone={toCardTone(statusTone(leave.status))}>
              <div className='flex items-start justify-between gap-3'>
                <div className='space-y-2 min-w-0 flex-1'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <StatusPill tone={getTypeTone(leave.type)}>{leave.type}</StatusPill>
                    <StatusPill tone={statusTone(leave.status)}>{leave.status}</StatusPill>
                  </div>
                  <div className='flex items-center gap-2 text-neutral-800 dark:text-white/80 flex-wrap text-sm'>
                    <Calendar className='h-4 w-4 text-neutral-400 dark:text-white/40' />
                    <span className='tabular-nums'>{formatDate(new Date(leave.startDate))} – {formatDate(new Date(leave.endDate))}</span>
                    <span className='text-neutral-400 dark:text-white/40'>({getDaysCount(leave.startDate, leave.endDate)} days)</span>
                  </div>
                  <p className='text-sm text-neutral-600 dark:text-white/60 whitespace-pre-wrap'>{leave.reason}</p>
                  {leave.rejectionReason && (
                    <p className='text-sm text-rose-600 dark:text-rose-300'>Rejection reason: {leave.rejectionReason}</p>
                  )}
                </div>
                {leave.status === 'PENDING' && (
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => handleCancel(leave.id)}
                    className='text-rose-600 dark:text-rose-300 hover:text-rose-700 dark:hover:text-rose-200 hover:bg-rose-500/10 shrink-0'
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                )}
              </div>
            </GlassCard>
          ))}
        </div>
      )}
        </>
      )}
    </PageShell>
  )
}
